/**
 * Brand theme loading for the Figma playground. See `stories.ts` for why playground-only
 * helpers live in this directory rather than in `utils/`.
 */

// Every generated brand theme, loaded one at a time. These are the self-contained
// <brand>.css files (base merged in), not the diff-only *.overrides.css ones: the
// switcher injects a single <style> element, so a brand swap has to replace the whole
// token layer instead of stacking on whichever brand was shown before it.
//
// The exclude spells the directory out instead of the shorter `!**/*.overrides.css`.
// Vite passes a negative glob that starts with `**` through unresolved, so what it ends
// up matching against depends on the common base it derives from the positive pattern —
// and on the test deploy it matched nothing, which put every *.overrides.css file in the
// brand switcher. A path-anchored exclude is resolved the same way the positive pattern
// is. `brandNames` below drops them a second time, so a switcher that disagrees with the
// glob is a unit test failure rather than something only a deploy shows.
const brandTokenFiles = import.meta.glob<string>(
  [
    '../node_modules/@tapp/design-tokens/figma-variables/*.css',
    '!../node_modules/@tapp/design-tokens/figma-variables/*.overrides.css',
  ],
  { query: '?inline', import: 'default' },
);
// The @font-face rules travel with the theme rather than being imported up front:
// different brands declare the same family name (PublicoHeadline, MuseoSans, OpenSans, …)
// pointing at different files, so loading all of them at once serves the wrong font.
const brandFontFiles = import.meta.glob<string>('../assets/styles/fonts/*_fonts.css', {
  query: '?inline',
  import: 'default',
});

const TOKEN_DIR = '../node_modules/@tapp/design-tokens/figma-variables';
const FONT_DIR = '../assets/styles/fonts';

// Generated files in figma-variables/ that are not a brand theme: the shared floor, the
// text-style classes, two Figma collections that hold no brand (spacing and the unnamed
// "variable collection"), and kiosk's primitive layer, which loads underneath kiosk.css
// — see PRIMITIVE_LAYERS below.
const NON_BRAND_FILES = new Set(['base', 'spacing', 'typography', 'variable collection', 'kiosk.nl']);

// Brands whose theme asks for a family that its own <brand>_fonts.css does not carry.
// kiosk_fonts.css is the pre-2026 PtSans set; AnekLatin is what the kiosk.nl theme names.
const FONT_STYLESHEETS: Record<string, string> = { kiosk: 'kiosk_aneklatin' };

// Themes that need a primitive layer loaded underneath, because they reference variables
// they do not declare themselves — kiosk's dark-blue/yellow palette and AnekLatin live in
// the kiosk.nl collection. Same order as apps/kiosk's figma-tokens.css.
const PRIMITIVE_LAYERS: Record<string, string[]> = { kiosk: ['kiosk.nl'] };

export const DEFAULT_BRAND = 'kiosk';

/**
 * The switcher's brand list, read off the generated theme filenames: a newly wired brand
 * shows up as soon as `pnpm generate:figma-variables` has written its theme.
 *
 * Takes the paths rather than reading the glob itself so the filtering is testable
 * without a bundler — see the note on `brandTokenFiles` for why that matters.
 */
export function brandNames(paths: string[]) {
  return paths
    .map((path) => path.slice(TOKEN_DIR.length + 1, -'.css'.length))
    .filter((name) => !name.endsWith('.overrides') && !NON_BRAND_FILES.has(name))
    .sort();
}

export const brands = brandNames(Object.keys(brandTokenFiles));

export function isBrand(name: unknown): name is string {
  return typeof name === 'string' && brands.includes(name);
}

export async function loadBrandCss(name: string) {
  const themes = [...(PRIMITIVE_LAYERS[name] ?? []), name].map((file) => `${TOKEN_DIR}/${file}.css`);
  const fonts = `${FONT_DIR}/${FONT_STYLESHEETS[name] ?? name}_fonts.css`;

  const parts = await Promise.all([brandFontFiles[fonts]?.(), ...themes.map((path) => brandTokenFiles[path]?.())]);

  return parts.filter(Boolean).join('\n');
}

/**
 * Literal `--token: #hex` declarations from a loaded brand theme. Aliases (`var(...)`)
 * are skipped: they resolve to another token that appears here in its own right.
 *
 * Mirrors extractTokenHexes in packages/design-tokens/scripts/brand-kit-drift.ts, which
 * does the same job at generate time to produce the committed drift data.
 */
export function extractTokenHexes(css: string) {
  return new Map(
    [...css.matchAll(/(--[a-z0-9-]+):\s*(#[0-9a-fA-F]{6})\b/g)].map((match) => [
      match[1] ?? '',
      (match[2] ?? '').toUpperCase(),
    ]),
  );
}

/** Literal values of the `--figma-fonts-*` tokens, e.g. `display` → `Quarto`. */
export function extractFontTokens(css: string) {
  return new Map(
    [...css.matchAll(/--figma-fonts-([a-z0-9-]+):\s*([^;]+);/g)]
      .filter((match) => !(match[2] ?? '').includes('var('))
      .map((match) => [match[1] ?? '', (match[2] ?? '').trim()]),
  );
}

/**
 * Families this brand's theme actually declares an `@font-face` for.
 *
 * The brand kits write a family the way a designer says it ("Flama Semicondensed",
 * "Anek Latin") while the `@font-face` uses the packed form ("FlamaSemicondensed",
 * "AnekLatin"), so a specimen styled with the kit's own string silently falls back to the
 * default serif. Matching against these names is what makes it render.
 */
export function extractFontFaceFamilies(css: string) {
  // Deduped: a family declares one @font-face per weight and style, so Roboto alone
  // accounts for six rules in some themes.
  return [
    ...new Set(
      [...css.matchAll(/@font-face\s*\{[^}]*?font-family:\s*([^;]+);/g)].map((match) =>
        (match[1] ?? '').trim().replace(/^['"]|['"]$/g, ''),
      ),
    ),
  ];
}

/**
 * Comparison form for a font family name: the brand kits write what a designer says
 * ("Anek Latin", "Flama Semicondensed") while the tokens and `@font-face` rules use the
 * packed form ("AnekLatin", "FlamaSemicondensed").
 */
export function packFontName(family: string) {
  return family.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/**
 * The name to style a specimen with: whichever declared `@font-face` family the brand kit
 * is naming, or the kit's own spelling when none matches — a system font such as Arial
 * has no `@font-face` and still renders.
 */
export function resolveFontFamily(family: string, declared: string[]) {
  const key = packFontName(family);

  return declared.find((candidate) => packFontName(candidate) === key) ?? family;
}

/**
 * Every `--token: value` declaration in a theme, last occurrence winning — which is what
 * the cascade does, and the whole point for a theme loaded over a primitive layer.
 *
 * Kiosk is the case that settles it: `kiosk.nl.css` aliases
 * `--figma-font-family-display` to `--figma-fonts-display`, which nothing ever declares,
 * and `kiosk.css` then re-aliases it to `--figma-fonts-body` (AnekLatin) because Kiosk
 * sets everything in one family. Taking the first declaration resolves to nothing at all.
 *
 * A token declared twice for a desktop breakpoint therefore reports its desktop value,
 * which is both what the cascade gives at that width and irrelevant to a font family.
 */
export function extractDeclarations(css: string) {
  const declarations = new Map<string, string>();

  for (const match of css.matchAll(/(--[a-z0-9-]+):\s*([^;]+);/g)) {
    const token = match[1] ?? '';
    if (token) declarations.set(token, (match[2] ?? '').trim());
  }

  return declarations;
}

/**
 * Follows a token's `var()` aliases to the value it ends at, e.g.
 * `--figma-typography-heading-h1-normal-font` → `--figma-font-family-display` →
 * `--figma-fonts-display` → `Quarto`.
 */
export function resolveTokenValue(declarations: Map<string, string>, token: string): string | undefined {
  let value = declarations.get(token);

  // Bounded rather than recursive: a token that aliases itself would otherwise hang the page.
  for (let step = 0; step < 10; step += 1) {
    const alias = value?.match(/^var\(\s*(--[a-z0-9-]+)\s*\)$/)?.[1];
    if (!alias) return value;

    value = declarations.get(alias);
  }

  return value;
}
